import { useEffect, useMemo, useState } from "react";
import ResourceList from "@/components/ResourceList";
import AssetAiAssistant from "@/components/AssetAiAssistant";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";

/**
 * Assets — 1:1 with the mobile Owner "Fleet" list.
 *
 * Backend contract:
 *   GET   /api/assets
 *   POST  /api/admin/assets                body: AssetIn
 *   PATCH /api/admin/assets/{id}
 *   GET   /api/site-config/me              → { asset_categories: string[] }
 *   PATCH /api/admin/site-config           → { asset_categories: string[] }
 *
 * Category ("Asset Type") is a site-scoped list stored on the site config.
 * Supervisor+ users can add a new category inline (Josh's Feb 23 spec —
 * e.g. "LV Trailer"); the write goes to /api/admin/site-config which the
 * production backend gates by role.
 */
const ADMIN_ROLES = ["admin", "control", "planner", "superintendent", "owner"];

// Module-scope so React doesn't re-mount the AI panel on every render of
// the Assets page (`react/no-unstable-nested-components`).
function AssetAiTopSlot({ form, setForm }) {
  return <AssetAiAssistant form={form} setForm={setForm} />;
}

export default function Assets() {
  const { user } = useAuth();
  const canAddType = can(user, "manageAssetTypes");

  // Live site-config asset_categories list — fetched once on mount and
  // extended in place when Supervisor+ adds a new one. Falls back to a
  // small default set if the fetch fails (so the create form still works).
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    let cancelled = false;
    api.get("/site-config/me")
      .then((r) => {
        if (cancelled) return;
        const list = Array.isArray(r.data?.asset_categories) ? r.data.asset_categories : [];
        setCategories(list);
      })
      .catch(() => {
        // Silent — the create dropdown will just show whatever the last
        // successful fetch had (or an empty list if this was the first).
      });
    return () => { cancelled = true; };
  }, []);

  // Persist a new asset category to the site config. Called from the
  // Register Asset form's inline "+ Add asset type" button.
  const addAssetType = async (name) => {
    const clean = String(name || "").trim();
    if (!clean) throw new Error("Type name required.");
    // Case-insensitive dedupe against the current list.
    const existing = categories.find((c) => c.toLowerCase() === clean.toLowerCase());
    if (existing) {
      // Already there — just make it selectable without a round-trip.
      return;
    }
    const next = [...categories, clean];
    await api.patch("/admin/site-config", { asset_categories: next });
    // Optimistically update — the caller relies on `options` having the
    // new value so the SelectAddable can auto-select it.
    setCategories(next);
  };

  const createConfig = useMemo(() => ({
    endpoint: "/admin/assets",
    singular: "Asset",
    title: "Register New Asset",
    roles: ADMIN_ROLES,
    topSlot: AssetAiTopSlot,
    fields: [
      { name: "id", label: "Asset ID / Tag", required: true, placeholder: "DIG-12", hint: "Short unique tag technicians will scan/type" },
      { name: "name", label: "Display name", required: true, placeholder: "Digger 12 · Northern Pit" },
      {
        name: "category",
        label: "Asset Type",
        type: "select-addable",
        required: true,
        options: categories,
        canAdd: canAddType,
        addLabel: "+ Add asset type",
        placeholder: "e.g. LV Trailer",
        onAddOption: addAssetType,
        hint: canAddType
          ? "Supervisor+ can add a new type on the fly (e.g. LV Trailer)"
          : "Contact your supervisor to add a new type",
      },
      { name: "asset_type", label: "Machine sub-type", placeholder: "excavator / haul-truck / wheel-loader" },
      { name: "machine_model", label: "Machine model", placeholder: "Hitachi EX8000-6" },
      { name: "serial_number", label: "Serial Number", placeholder: "e.g. 12A-04512", hint: "Manufacturer's plate number — appears on every PDF report" },
      {
        name: "engine_config",
        label: "Engine Config",
        type: "select",
        options: ["Single", "Twin"],
        placeholder: "Single or Twin",
      },
      { name: "engine_count", label: "Engine count", type: "number", min: 0 },
      { name: "bucket_number", label: "Bucket / GET set", placeholder: "B-04" },
    ],
  }), [categories, canAddType]);

  return (
    <ResourceList
      endpoint="/assets"
      title="Assets"
      subtitle="Registry · Fleet"
      resourceName="Asset"
      itemLabel={(it) => it.name || it.asset_name || it.id || "Asset"}
      itemMeta={(it) => [
        { label: "ID", value: it.id || it.asset_id },
        { label: "Type", value: it.category },
        { label: "Sub-type", value: it.asset_type },
        { label: "Model", value: it.machine_model },
        { label: "Serial", value: it.serial_number },
        { label: "Engine", value: it.engine_config },
        { label: "Bucket", value: it.bucket_number },
      ]}
      statusOf={(it) => (it.retired ? "retired" : it.status || "active")}
      searchFields={["name", "id", "asset_id", "category", "asset_type", "machine_model", "serial_number"]}
      testid="assets-page"
      emptyLabel="No assets registered."
      createConfig={createConfig}
    />
  );
}
